import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import {
  CustomNodeEditorContext,
  defaultCustomNodeFeedVersion,
} from "@/components/canvas-editor/utils";
import { HistoryItem } from "@/components/HistoryItem";
import { Page } from "@/components/Page";
import { ButtonLink } from "@/components/ui/button";
import { H1 } from "@/components/ui/typography";
import {
  CustomNodeHistoryItem,
  editCustomNode,
  getCustomNodeHistoryQueryOptions,
  getCustomNodeQueryOptions,
} from "@/lib/api";

export const Route = createFileRoute(
  "/app/_sidebar-layout/custom-nodes/$nodeId/version-history",
)({
  component: RouteComponent,
  loader: async ({ context: { queryClient }, params: { nodeId } }) => {
    await Promise.all([
      queryClient.ensureQueryData(
        getCustomNodeHistoryQueryOptions(parseInt(nodeId)),
      ),
      queryClient.ensureQueryData(getCustomNodeQueryOptions(parseInt(nodeId))),
    ]);
  },
  head: () => ({ meta: [{ title: "Custom Node Version History" }] }),
});

function NodeHistoryItem({
  node,
  nodeId,
}: {
  node: CustomNodeHistoryItem;
  nodeId: string;
}) {
  const navigate = useNavigate({
    from: "/app/custom-nodes/$nodeId/version-history",
  });
  const onRestore = useMutation({
    mutationFn: async () => {
      await editCustomNode({
        ...node,
        id: parseInt(nodeId),
        component_metadata: node.component_metadata,
      });
    },
    onSuccess: () =>
      navigate({
        to: "/app/custom-nodes/$nodeId/view",
        params: { nodeId },
      }),
  });

  return (
    <HistoryItem
      feedVersion={{
        ...defaultCustomNodeFeedVersion,
        created_at: node.created_at,
        manifest: {
          filter: node.component,
        },
      }}
      mutation={onRestore}
      description={node.version_message}
    />
  );
}

function RouteComponent() {
  const { nodeId } = Route.useParams();
  const { data } = useSuspenseQuery(
    getCustomNodeHistoryQueryOptions(parseInt(nodeId)),
  );
  const { data: node } = useSuspenseQuery(
    getCustomNodeQueryOptions(parseInt(nodeId)),
  );

  return (
    <Page>
      <div className="flex flex-col gap-2 border-b border-slate-6 dark:border-slatedark-6 pb-4">
        <div className="text-slate-dim">Version History</div>
        <div className="flex items-center gap-2 justify-between">
          <H1 className="border-b-0 flex-1">{node.title}</H1>
          <div className="flex items-center gap-2">
            <ButtonLink to="/app/custom-nodes/$nodeId/view" params={{ nodeId }}>
              Exit Version History
            </ButtonLink>
          </div>
        </div>
      </div>

      <CustomNodeEditorContext value={true}>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((item) => (
            <NodeHistoryItem key={item.id} node={item} nodeId={nodeId} />
          ))}
        </div>
      </CustomNodeEditorContext>
    </Page>
  );
}
